import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { Grid, Typography, IconButton, gridClasses, Box, Tooltip } from '@mui/material';
import ModalDialog from '../ui/ModalDialog/index';
import { CancelPresentationRounded, DeleteRounded, PersonAddAlt, SaveRounded } from '@mui/icons-material';
import PropTypes from 'prop-types';
import styles from './styles';
import Datagrid from '../ui/Datagrid';
import columns from './table_config';
import SelectEditInputTextField from '../ui/SelectEditInputTextField';
import DeleteConfirmationModal from '../deleteConfirmationModal';
import { addNewWindowRow, createWindow, deleteWindowRow, importWellsWindowCSVFIle, listWindow, updateWindow } from '../../store/actions/wellsWindow';
import { enqueueSnackbar } from '../../store/actions/snackbar';
import FileUploadDialog from '../ui/FileUploadDialog';
import useUnsavedChanges from '../../hooks/useUnsavedChanges';
import useUnitConversion from '../../hooks/useUnitConversion';
import { UNIT_SYSTEM } from '../../shared/config/hydraulics_constants';
import { APP_ACTION_TYPES, HYDRAULICS_MAX_FILE_SIZE, PERMISSION_DIALOG_SOURCES, TOOLTIPS } from '../../utils/constants';
import PermissionsModal from '../permissions';
import { checkHydraulicsAdmin, checkPermissions } from '../../utils/checkPermissions';
import CreatedByInfoDialog from '../ui/CreatedByInfoDialog';
import { REDUX_ACTIONS } from '../../shared/config/constants';

const WindowModal = ({
  onCloseClick,
  windowData,
  isOpen
}) => {
  const dispatch = useDispatch();
  const classes = styles();
  const window = useSelector((state) => state.wellsWindow);
  const apiRef = useGridApiRef();
  const [properties, setProperties] = useState([]);
  const [invalidRows, setInvalidRows] = useState({});
  const [uploadOpen, setUploadOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteAction, setDeleteAction] = useState(false);
  const [deleteRow, setDeleteRow] = useState();
  const { unit, convertUnitSystem } = useUnitConversion();
  const {
    unSavedExists,
    handleClose,
    UnsavedChangesModal
  } = useUnsavedChanges(properties, onCloseClick);
  const disableAction = properties.some(item => item?.isDirty === true || item?.isNewRow);
  const user = useSelector((state) => state.currentUser);
  const [permissions, setPermissions] = useState({
    open: false,
    data: {}
  });
  const isHydraulicsAdmin = checkHydraulicsAdmin(
    user?.type,
    user?.accessLevels
  );

  useEffect(() => {
    if (!window.loading) {
      dispatch(listWindow(windowData?.row?._id, onCloseClick));
    }
    return () => {
      dispatch({
        type: REDUX_ACTIONS.WELLS_WINDOW_RESET
      });
    };
  }, []);

  useEffect(() => {
    if (window?.data) {
      setProperties(window?.data);
    }
  }, [window?.data]);

  useEffect(() => {
    if (properties[0]?.md === '') {
      const id = properties[0]._id;
      setTimeout(() => {
        apiRef.current.scrollToIndexes({
          rowIndex: 0
        });
        apiRef.current.setCellMode(id, 'md', 'edit');
        setTimeout(() => {
          apiRef.current.setCellFocus(id, 'md');
        }, 50);
      }, 50);
      apiRef.current.setPage(0);
    }
  }, [properties?.length]);

  const handleAddWindow = () => {
    dispatch(addNewWindowRow());
  };

  const removeWindow = (row) => {
    if (row?.isNewRow) {
      const updatedWindow = properties.filter(item => item._id !== row?._id);
      setProperties(updatedWindow);
      dispatch(listWindow(windowData?.row?._id));
    } else {
      dispatch(deleteWindowRow(row?._id, windowData?.row?._id));
    }
    setDeleteAction(false);
    setDeleteRow({});
  };

  const showError = (message, rowId) => {
    dispatch(enqueueSnackbar(message, 'error'));
    setInvalidRows(prev => ({ ...prev, [rowId]: true }));
    return false;
  };

  const checkMD = (row, rowIndex, properties) => {
    const prevRow = rowIndex > 0 ? properties[rowIndex - 1] : null;
    if (prevRow && row.md <= prevRow.md) {
      showError('Measured Depth should be greater than the previous row\'s Measured Depth', row._id);
      return false;
    }
    return true;
  };

  const checkFP = (row) => {
    if (row.fp <= row.pp) {
      showError('Fracture Pressure should be greater than Pore Pressure', row._id);
      return false;
    }
    const maxFp = window?.units?.fp?.max;
    if (row.fp >= maxFp) {
      showError(`Fracture Pressure should be less than ${maxFp} ${window?.units?.fp?.label}`, row._id);
      return false;
    }
    return true;
  };

  const checkPP = (row) => {
    const minPp = window?.units?.pp?.min;
    if (row.pp <= minPp) {
      showError(`Pore Pressure should be greater than ${minPp} ${window?.units?.pp?.label}`, row._id);
      return false;
    }
    return true;
  };

  const validateWindowData = (row) => {
    const rowIndex = properties.findIndex(item => item._id === row._id);

    if (rowIndex === 0) {
      if (row.md < 0) {
        return showError('The first entered Measured Depth should be equal or greater than 0.0', row._id);
      }
    }

    if (row.isNewRow) {
      if (properties.length === 1) {
        return checkFP(row) && checkPP(row);
      }
      if (!checkMD(row, properties.length, properties) || !checkFP(row) || !checkPP(row)) {
        return false;
      }
    } else {
      if (!checkMD(row, rowIndex, properties) || !checkFP(row) || !checkPP(row)) {
        return false;
      }
    }
    return true;
  };

  const checkWindowDataValues = (row) => {
    const invalidFields = [];
    if (row?.md === undefined || row?.md === null || row?.md === '') invalidFields.push('Measured Depth');
    if (row?.pp === undefined || row?.pp === null || row?.pp === '') invalidFields.push('Pore Pressure');
    if (row?.fp === undefined || row?.fp === null || row?.fp === '') invalidFields.push('Fracture Pressure');

    if (invalidFields.length === 0) {
      setInvalidRows(prev => ({ ...prev, [row._id]: false }));
      for (const key in row) {
        if (typeof row[key] === 'string' && !isNaN(row[key])) {
          row[key] = Number(row[key]);
        }
      }
      return validateWindowData(row);
    } else {
      setInvalidRows(prev => ({ ...prev, [row._id]: true }));
      dispatch(
        enqueueSnackbar(
          `Please enter the following details: ${invalidFields.join(', ')}`,
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return false;
    }
  };

  const saveWindow = (row) => {
    if (checkWindowDataValues(row)) {
      const keysToExtract = ['md', 'fp', 'pp'];

      const extracted = Object.fromEntries(
        Object.entries(row).filter(([key]) => keysToExtract.includes(key))
      );

      // eslint-disable-next-line multiline-ternary
      const converted = unit !== UNIT_SYSTEM.US ? {
        md: convertUnitSystem(extracted.md, window?.units?.md.conversionFactor),
        fp: convertUnitSystem(extracted.fp, window?.units?.fp.conversionFactor),
        pp: convertUnitSystem(extracted.pp, window?.units?.pp.conversionFactor),
      } : null;

      const dataToSave = unit !== UNIT_SYSTEM.US ? converted : extracted;
      if (row?.isNewRow) {
        dispatch(createWindow(dataToSave, windowData?.row?._id));
      } else {
        dispatch(updateWindow(dataToSave, windowData?.row?._id, row?._id));
      }
    }
  };

  const closeHandler = () => {
    if (unSavedExists) {
      handleClose(false);
      return;
    }
    setProperties([]);
    onCloseClick();
  };

  const getModalTitle = () => {
    return (
        <Grid container alignItems="center" justifyContent="space-between">
          <Grid item>
            {windowData?.row?.name || ''} | Drilling Window <CreatedByInfoDialog data={window?.createdDetails} loading={window?.loading || disableAction} />
          </Grid>
          <Grid item>
          {isHydraulicsAdmin
            ? (
              <Tooltip title={TOOLTIPS.viewPermissions}>
                <span>
                  <IconButton
                    variant="solid"
                    key="permissions"
                    disabled={window?.loading || disableAction || properties?.length === 0}
                    sx={{ color: window?.permissionId && 'green' }}
                    onClick={() => {
                      setPermissions({
                        open: true,
                        data: {
                          ...windowData?.row,
                          _id: window?._id,
                          permissionId: window?.permissionId
                        }
                      });
                    }}
                  >
                    <PersonAddAlt fontSize='large' />
                  </IconButton>
                </span>
              </Tooltip>
              )
            : null}
            <IconButton
              onClick={closeHandler}
            >
              <CancelPresentationRounded fontSize='large' />
            </IconButton>
          </Grid>
        </Grid>
    );
  };
  const renderActions = ({
    row
  }) => {
    return (
      <Grid container justifyContent="center">
        <IconButton
          variant="solid"
          disabled={!row.isDirty || window?.loading || (!row.isNewRow && !checkPermissions(APP_ACTION_TYPES.UPDATE, window?.permissions))}
          onClick={() => {
            saveWindow(row);
          }}
        >
          <SaveRounded />
        </IconButton>
        <IconButton
          variant="solid"
          disabled={window?.loading || (!row.isNewRow && !checkPermissions(APP_ACTION_TYPES.DELETE, window?.permissions))}
          onClick={() => {
            setDeleteAction(true);
            setDeleteRow(row);
          }}
        >
          <DeleteRounded />
        </IconButton>
      </Grid>
    );
  };

  const updateData = (id, field, value) => {
    setProperties(prevProperties => {
      const updatedProperties = prevProperties.map(item => {
        if (item._id === id) {
          const isDirty = value !== item[field];
          return {
            ...item,
            [field]: Number(value),
            isDirty
          };
        }
        return item;
      });
      return updatedProperties;
    });
  };

  const handleOpenUploadModal = () => {
    setUploadOpen(true);
  };

  const handleUploadSuccess = () => {
    setUploadOpen(false);
  };

  const handleUploadCsv = (selectedFile) => {
    if (selectedFile.size >= HYDRAULICS_MAX_FILE_SIZE.ONE_MB_BYTE) {
      dispatch(
        enqueueSnackbar(
          'File must be less than 1 MB ',
          'error',
          new Date().getTime() + Math.random()
        )
      );
    } else {
      dispatch(importWellsWindowCSVFIle(selectedFile, windowData?.row?._id, setIsLoading, handleUploadSuccess));
    }
  };

  const renderValueCell = (params) => {
    return <SelectEditInputTextField numberFormatOnly {...params} updateData={updateData} />;
  };

  if (apiRef.current === null) apiRef.current = {};
  return (
    <ModalDialog
      title={getModalTitle()}
      isOpen={isOpen}
      onClose={closeHandler}
      dialogClass={classes.dialogModal}
    >
      <Grid container>
        <Box className={classes.customTool} sx={{ display: !checkPermissions(APP_ACTION_TYPES.CREATE, window?.permissions) && 'none !important' }}>
          <Box>
          <Typography variant='button' fontWeight='700' className={window.loading || disableAction ? classes.disableIcon : ''} onClick={handleAddWindow}>+ Add</Typography>
          </Box>
          <Box>
          <Typography
            variant="body1"
            fontWeight="500"
            sx={{ marginLeft: '8px', marginRight: '8px', fontSize: '17px' }}
          >
            |
          </Typography>
          </Box>
          <Box>
          <label className={window.loading || disableAction ? classes.disableImportLabel : classes.importLabel} onClick={() => {
            handleOpenUploadModal();
          }}>Import</label>
          </Box>
        </Box>
        <Grid item xs={12}>
        {
          deleteAction && <DeleteConfirmationModal
            open={deleteAction}
            handleDelete={removeWindow}
            handleClose={setDeleteAction}
            deleteRow={deleteRow}
          />
        }
        <UnsavedChangesModal />
          <Datagrid
            apiRef={apiRef}
            data={properties}
            sx={{
              marginLeft: '-6px',
              '.MuiDataGrid-cell': {
                fontSize: '1rem',
                fontWeight: 900
              },
              '.MuiDataGrid-columnHeaderTitle': {
                fontSize: '1.2rem',
                fontWeight: '900 !important',
              },
              [`& .${gridClasses.cell}`]: {
                py: 1,
              },
            }}
            loading={window.loading}
            columns={columns(renderValueCell, renderActions, window.units, unit)}
            onRowClick={(params, evt) => {
              evt.preventDefault();
              evt.stopPropagation();
            }}
            disableStickyHeader={false}
            autoHeight={false}
            autoheight
            height={'50vh'}
            getRowClassName={(params) => {
              return invalidRows[params.id] ? classes.invalidRow : '';
            }}
            pageSize={100}
            editMode="cell"
            toolbar={{
              options: {
                columns: false,
                filters: false,
                density: false,
                export: false
              },
            }}
            enableDirtyLock
            permissions={window?.permissions}
          />
        </Grid>
        {uploadOpen && (
          <FileUploadDialog
          open={uploadOpen}
          handleClose={() => {
            setUploadOpen(false);
          }}
          isLoading={isLoading}
          handleUpload={handleUploadCsv}
          fileSize={HYDRAULICS_MAX_FILE_SIZE.ONE_MB}
        />
        )}
        {permissions?.open
          ? (
            <PermissionsModal
              isOpen={permissions?.open}
              data={
                {
                  ...permissions?.data,
                  collection: PERMISSION_DIALOG_SOURCES?.wellsWindows,
                }
              }
              onCloseClick={() => {
                setPermissions({
                  open: false,
                  data: {}
                });
              }}
              refreshParentDialog={() => {
                dispatch(listWindow(windowData?.row?._id));
              }}
            />
            )
          : null}
      </Grid>
    </ModalDialog>
  );
};
WindowModal.propTypes = {
  onCloseClick: PropTypes.func.isRequired,
  windowData: PropTypes.object,
  isOpen: PropTypes.bool.isRequired
};
WindowModal.defaultProps = {
  windowData: {}
};
export default WindowModal;
